import { motion } from "framer-motion";
import { Wifi, WifiOff } from "lucide-react";

/**
 * Centered overlay shown when an active match WebSocket has dropped.
 * The grace window before forfeit is decided server-side; we just visualize
 * the countdown the client is using for its auto-reconnect attempts.
 *
 * NOTE: this is intentionally NOT a "DISCONNECTED" / "DEFEAT" view. Those only
 * appear AFTER the grace window expires and the server confirms the forfeit.
 */
export default function ReconnectingOverlay({ secondsLeft = 6, total = 6 }) {
  const pct = Math.max(0, Math.min(100, (secondsLeft / total) * 100));
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[120] flex items-center justify-center bg-[#02040D]/70 backdrop-blur-md"
      data-testid="reconnecting-overlay"
      role="dialog"
      aria-live="polite"
    >
      <motion.div
        initial={{ scale: 0.9, y: 16 }}
        animate={{ scale: 1, y: 0 }}
        className="sm-card px-10 py-9 text-center max-w-sm w-[88%]"
        style={{ borderColor: "rgba(59,130,246,0.55)", boxShadow: "0 0 50px rgba(59,130,246,0.45)" }}
      >
        <div className="mx-auto w-16 h-16 grid place-items-center rounded-2xl border border-blue-500/40 bg-blue-500/10 mb-5">
          {secondsLeft > 0 ? (
            <motion.div animate={{ opacity: [1, 0.35, 1] }} transition={{ duration: 1.2, repeat: Infinity }}>
              <Wifi className="text-blue" size={28} />
            </motion.div>
          ) : (
            <WifiOff className="text-rose-500" size={28} />
          )}
        </div>

        <div className="font-mono text-[11px] tracking-[0.3em] text-blue uppercase mb-2">
          Connection unstable
        </div>
        <div className="font-heading font-black text-3xl text-white">
          Reconnecting<span className="inline-block w-9 text-left text-blue tabular-nums" data-testid="reconnect-countdown">… {secondsLeft}</span>
        </div>
        <div className="text-soft text-sm mt-3">
          Hang tight — we'll restore your match if you come back within the grace window.
        </div>

        {/* Countdown bar */}
        <div className="mt-6 h-1.5 rounded-full bg-white/10 overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500"
            initial={{ width: "100%" }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.8, ease: "linear" }}
          />
        </div>
      </motion.div>
    </motion.div>
  );
}
